// ─── Shared types and user model ─────────────────────────────────────────────
// Used by ALL apps (business, platform-admin). Do NOT add app-specific routes here.

export type UserRole = "super_admin" | "staff";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatarUrl?: string;
  role: UserRole;
  password?: string;
  status: "active" | "invited";
  joined?: string;
  mustChangePassword?: boolean;
  isOwner?: boolean;
  businessStatus?: "active" | "suspended" | "blocked";
  allowedModules?: string[];
}

export interface StaffInvite {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: Exclude<UserRole, "super_admin">;
  invitedBy: string;
  invitedAt: string;
  token: string;
  status: "pending" | "accepted";
}

/**
 * Resolves the display label for a user based on their role and ownership status.
 * Replaces the static `roleLabels` record to account for the `isOwner` flag.
 */
export function resolveRoleLabel(user: Pick<AuthUser, 'role' | 'isOwner'>): string {
  if (user.role === 'super_admin') return 'Super Admin';
  if (user.isOwner) return 'Owner';
  return 'Staff';
}

/** @deprecated Use `resolveRoleLabel(user)` instead — it accounts for `isOwner`. */
export const roleLabels: Record<UserRole, string> = {
  super_admin: "Super Admin",
  staff: "Staff",
};

// super_admin = platform admin (full platform access)
// staff       = business app user (permissions come from roleId + branch assignments)
export const rolePermissions: Record<UserRole, string[]> = {
  super_admin: [
    "platform-dashboard", "platform-businesses", "platform-plans",
    "platform-billing", "platform-analytics", "platform-support",
    "platform-notifications", "platform-logs", "platform-settings", "profile",
  ],
  staff: ["dashboard", "notifications", "profile"],
};

// ─── Business app route → permission key ─────────────────────────────────────
// Only routes that belong to apps/business live here.
export const businessRoutePermissions: Record<string, string> = {
  "/": "dashboard",
  "/vehicles": "vehicles",
  "/customers": "customers",
  "/bookings": "bookings",
  "/payments": "payments",
  "/pricing": "pricing",
  "/maintenance": "maintenance",
  "/reports": "reports",
  "/notifications": "notifications",
  "/documents": "documents",
  "/logs": "logs",
  "/settings": "settings",
  "/upgrade": "upgrade",
  // Account/profile management is always available regardless of plan modules —
  // it must stay reachable so users can change their password (e.g. the forced
  // password change after first sign-in) even on restricted plans.
  "/profile": "profile",
  "/agreements": "agreements",
  "/communications": "communications",
  "/exports": "exports",
  "/qr-access": "qr-access",
  "/gps": "gps",
  "/billing": "billing-center",
  "/imports": "imports",
  "/availability": "availability",
  "/wallets": "wallets",
  "/business-setup": "business-setup",
  "/tracking": "gps",
  "/notes": "documents",
};

// ─── Platform-admin route → permission key ───────────────────────────────────
// Only routes that belong to apps/platform-admin live here.
export const platformAdminRoutePermissions: Record<string, string> = {
  "/console": "platform-dashboard",
  "/console/businesses": "platform-businesses",
  "/console/plans": "platform-plans",
  "/console/billing": "platform-billing",
  "/console/analytics": "platform-analytics",
  "/console/support": "platform-support",
  "/console/notifications": "platform-notifications",
  "/console/logs": "platform-logs",
  "/console/settings": "platform-settings",
  "/console/security": "platform-settings",
  "/console/users": "platform-businesses",
  "/console/health": "platform-analytics",
  "/console/onboarding-funnel": "platform-analytics",
  "/console/cms": "platform-support",
  "/console/testimonials": "platform-support",
  "/console/legal-pages": "platform-support",
  "/console/email-templates": "platform-support",
  "/console/profile": "profile",
};

// ─── Combined map used by AuthProvider (canAccessRoute) ──────────────────────
// AuthProvider is shared, so it needs both maps merged. Adding a new route to
// one app only requires editing that app's section above — not this combined map.
export const routePermissions: Record<string, string> = {
  ...businessRoutePermissions,
  ...platformAdminRoutePermissions,
};

// ─── Backend permission → route keys (business app only) ─────────────────────
const backendPermissionToRouteKeys: Record<string, string[]> = {
  "booking access":    ["bookings", "availability", "trips", "agreements", "dashboard"],
  "vehicle management": ["vehicles", "gps", "pricing", "dashboard"],
  "customer access":   ["customers", "wallets", "dashboard"],
  "financial access":  ["payments", "billing-center", "wallets", "dashboard"],
  "staff management":  ["business-setup", "dashboard"],
  "reporting access":  ["reports", "logs", "dashboard"],
  "report access":     ["reports", "logs", "dashboard"],
  "maintenance access": ["maintenance", "dashboard"],
  "document access":   ["documents", "agreements", "dashboard"],
};

// ─── Module → Route Key mapping (business app plan-based access) ─────────────
export const MODULE_ROUTE_MAP: Record<string, string[]> = {
  vehicles:      ["vehicles", "availability", "trips", "imports", "gps"],
  bookings:      ["bookings"],
  customers:     ["customers", "wallets"],
  payments:      ["payments", "billing-center", "pricing", "agreements", "exports"],
  reports:       ["reports", "logs"],
  maintenance:   ["maintenance"],
  documents:     ["documents"],
  notifications: ["notifications"],
  settings:      ["settings", "business-setup"],
  qr:            ["qr-access"],
};

// ─── Always-on modules (never gated by plan) ────────────────────────────────
// Must stay in sync with ALWAYS_ON_MODULES in useEffectiveAccess hook.
const ALWAYS_ON_MODULES: string[] = ["settings", "notifications", "reports"];

export function resolveEffectiveRoutePermissions(user: AuthUser): string[] {
  if (user.role === "super_admin") {
    return rolePermissions.super_admin;
  }

  const base = new Set<string>(["dashboard", "notifications", "profile"]);

  // Always-on modules are accessible regardless of plan
  for (const mod of ALWAYS_ON_MODULES) {
    const routeKeys = MODULE_ROUTE_MAP[mod] ?? [];
    for (const key of routeKeys) base.add(key);
  }

  const modules = Array.isArray(user.allowedModules) ? user.allowedModules : [];

  // Plan fetch failed at login — restrict to base routes only until session refreshes
  if (modules.includes("__fetch_failed__")) {
    return [...base];
  }

  if (modules.length > 0) {
    for (const mod of modules) {
      const routeKeys = MODULE_ROUTE_MAP[mod] ?? [];
      for (const key of routeKeys) base.add(key);
    }
  }

  return [...base];
}

export function hasOperationalPermission(user: AuthUser | null, _permission: string): boolean {
  if (!user) return false;
  if (user.role === "super_admin") return true;
  // Single-owner system: business owner has full access
  if (user.isOwner) return true;
  if (user.role === "staff") return true;
  return false;
}
